/**
 * What a version compare looks at: the text only, or the text plus the
 * rendered pages (pixel compare, for artwork).
 *
 * Versions saved from Papercurve Office are converted to PDF on every save,
 * and two conversions of the same text render a fraction of a pixel apart.
 * Pixel compare reports that as changes, so Office documents compare text
 * only by default. The user's choice is remembered separately for Office
 * documents and for everything else, so turning artwork on for a carton does
 * not bring the noise back on invoices.
 */

export var COMPARE_MODES = {
  TEXT: "text",
  ARTWORK: "artwork",
};

var DOCUMENT_KINDS = {
  OFFICE: "office",
  OTHER: "other",
};

var STORAGE_KEYS = {
  office: "versionCompareModeOffice",
  other: "versionCompareModeOther",
};

var DEFAULT_MODES = {
  office: COMPARE_MODES.TEXT,
  other: COMPARE_MODES.ARTWORK,
};

// The file types Papercurve Office edits
var OFFICE_MIME_TYPES = [
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];

/**
 * A pair is an Office document when either version is an Office file.
 *
 * @param {Array<string|null|undefined>} mimeTypes - the two versions' mime types
 * @returns {string} "office" or "other"
 */
export function documentKindOf(mimeTypes) {
  var isOffice = mimeTypes.some(function (mimeType) {
    return OFFICE_MIME_TYPES.indexOf(mimeType) !== -1;
  });
  return isOffice ? DOCUMENT_KINDS.OFFICE : DOCUMENT_KINDS.OTHER;
}

/**
 * The remembered compare mode for a kind of document, or its default.
 *
 * @param {string} kind - from documentKindOf
 * @returns {string} a COMPARE_MODES value
 */
export function loadCompareMode(kind) {
  var stored = localStorage.getItem(STORAGE_KEYS[kind]);
  if (stored === COMPARE_MODES.TEXT || stored === COMPARE_MODES.ARTWORK) {
    return stored;
  }
  return DEFAULT_MODES[kind];
}

/**
 * Remembers the user's compare mode for a kind of document.
 *
 * @param {string} kind - from documentKindOf
 * @param {string} mode - a COMPARE_MODES value
 */
export function saveCompareMode(kind, mode) {
  localStorage.setItem(STORAGE_KEYS[kind], mode);
}
